import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { getMovieById, getMoviesByCategory } from '@/mocks/movies';
import { getMoviePosterUrl } from '@/mocks/posters';

function getCategoryLabel(key: string) {
  const map: Record<string, string> = {
    movie: '电影',
    tv: '电视剧',
    variety: '综艺',
    anime: '动漫',
    doc: '纪录片',
  };
  return map[key] || key;
}

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const [imgError, setImgError] = useState(false);
  const [activeTab, setActiveTab] = useState<'info' | 'cast'>('info');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const movie = getMovieById(id || '');

  if (!movie) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <i className="ri-error-warning-line text-5xl text-foreground-300 block mb-4"></i>
            <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">资源未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">该影视资源不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const posterUrl = getMoviePosterUrl(movie);

  const relatedMovies = getMoviesByCategory(movie.category)
    .filter((m) => m.id !== movie.id)
    .slice(0, 6);

  return (
    <>
      <Navbar />

      <main className="pt-16">
        <div className="w-full px-4 md:px-6 bg-background-50 border-b border-background-200">
          <Breadcrumb
            items={[
              { label: getCategoryLabel(movie.category), href: `/category/${movie.category}` },
              { label: movie.title },
            ]}
          />
        </div>

        <section className="py-8 md:py-12 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="w-full md:w-[300px] shrink-0">
                <div className="relative aspect-[2/3] rounded-xl overflow-hidden bg-background-200">
                  {!imgError ? (
                    <img
                      src={posterUrl}
                      alt={`${movie.title} 海报`}
                      title={`${movie.title} - 丫丫影院`}
                      className="w-full h-full object-cover object-top"
                      onError={() => setImgError(true)}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-foreground-400">
                      <i className="ri-film-line text-5xl"></i>
                    </div>
                  )}
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 text-sm font-bold bg-primary-500 text-background-50 rounded-lg">
                      {movie.rating} 分
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-1">
                  {movie.title}
                </h1>
                <p className="text-sm text-foreground-500 mb-4">{movie.titleEn}</p>

                <div className="flex flex-wrap items-center gap-2 mb-5">
                  {movie.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                  {movie.isNew && (
                    <span className="px-3 py-1 text-xs bg-accent-100 text-accent-700 rounded-full font-medium">
                      NEW
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6 mb-6 text-sm">
                  <div className="flex">
                    <span className="text-foreground-500 w-20 shrink-0">导演：</span>
                    <span className="text-foreground-950">{movie.director}</span>
                  </div>
                  <div className="flex">
                    <span className="text-foreground-500 w-20 shrink-0">主演：</span>
                    <span className="text-foreground-950">{movie.cast.join(' / ')}</span>
                  </div>
                  <div className="flex">
                    <span className="text-foreground-500 w-20 shrink-0">类型：</span>
                    <span className="text-foreground-950">{getCategoryLabel(movie.category)}</span>
                  </div>
                  <div className="flex">
                    <span className="text-foreground-500 w-20 shrink-0">片长：</span>
                    <span className="text-foreground-950">{movie.duration}</span>
                  </div>
                  <div className="flex">
                    <span className="text-foreground-500 w-20 shrink-0">地区：</span>
                    <span className="text-foreground-950">{movie.region}</span>
                  </div>
                  <div className="flex">
                    <span className="text-foreground-500 w-20 shrink-0">年份：</span>
                    <span className="text-foreground-950">{movie.year}</span>
                  </div>
                  <div className="flex">
                    <span className="text-foreground-500 w-20 shrink-0">上映：</span>
                    <span className="text-foreground-950">{movie.releaseDate}</span>
                  </div>
                  <div className="flex">
                    <span className="text-foreground-500 w-20 shrink-0">更新：</span>
                    <span className="text-foreground-950">{movie.updateDate}</span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="#"
                    className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-play-circle-line text-lg"></i>
                    立即播放
                  </a>
                  <a
                    href="#"
                    className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-secondary-100 text-foreground-800 rounded-full text-sm font-medium hover:bg-secondary-200 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-heart-line text-lg"></i>
                    收藏
                  </a>
                  <a
                    href="#"
                    className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-secondary-100 text-foreground-800 rounded-full text-sm font-medium hover:bg-secondary-200 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-share-forward-line text-lg"></i>
                    分享
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-100 border-b border-background-200">
          <div className="w-full px-4 md:px-6">
            <div className="flex gap-0">
              <button
                onClick={() => setActiveTab('info')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                  activeTab === 'info'
                    ? 'border-primary-500 text-primary-500'
                    : 'border-transparent text-foreground-500 hover:text-foreground-800'
                }`}
              >
                剧情简介
              </button>
              <button
                onClick={() => setActiveTab('cast')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                  activeTab === 'cast'
                    ? 'border-primary-500 text-primary-500'
                    : 'border-transparent text-foreground-500 hover:text-foreground-800'
                }`}
              >
                演职人员
              </button>
            </div>
          </div>
        </section>

        <section className="py-8 md:py-10 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-4xl">
            {activeTab === 'info' && (
              <div>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed whitespace-pre-line">
                  {movie.longDescription}
                </p>
              </div>
            )}
            {activeTab === 'cast' && (
              <div>
                <h3 className="text-lg font-heading font-bold text-foreground-950 mb-5">导演</h3>
                <div className="flex flex-wrap gap-3 mb-8">
                  <span className="px-4 py-2 bg-background-100 text-foreground-800 rounded-lg text-sm">
                    {movie.director}
                  </span>
                </div>
                <h3 className="text-lg font-heading font-bold text-foreground-950 mb-5">主演</h3>
                <div className="flex flex-wrap gap-3">
                  {movie.cast.map((actor) => (
                    <span
                      key={actor}
                      className="px-4 py-2 bg-background-100 text-foreground-800 rounded-lg text-sm hover:bg-primary-100 hover:text-primary-700 transition-colors cursor-pointer"
                    >
                      {actor}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        {relatedMovies.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100">
            <div className="w-full px-4 md:px-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-6 bg-primary-500 rounded-full"></div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                  同类推荐
                </h2>
              </div>
              <div
                className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5"
                data-product-shop
              >
                {relatedMovies.map((m) => (
                  <MovieCard key={m.id} movie={m} />
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-4xl">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
              常见问题
            </h2>
            <div className="space-y-3">
              <details className="group rounded-xl bg-background-100 p-5 cursor-pointer">
                <summary className="text-sm font-medium text-foreground-900 list-none flex items-center justify-between">
                  {movie.title}可以在线观看吗？
                  <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform"></i>
                </summary>
                <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                  是的，{movie.title}可以在丫丫影院高清免费在线观看，播放流畅不缓冲，支持多种清晰度选择。
                </p>
              </details>
              <details className="group rounded-xl bg-background-100 p-5 cursor-pointer">
                <summary className="text-sm font-medium text-foreground-900 list-none flex items-center justify-between">
                  {movie.title}的画质如何？
                  <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform"></i>
                </summary>
                <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                  丫丫影院提供{movie.title}的高清画质在线观看，画面清晰细腻，为您带来极致观影体验。
                </p>
              </details>
              <details className="group rounded-xl bg-background-100 p-5 cursor-pointer">
                <summary className="text-sm font-medium text-foreground-900 list-none flex items-center justify-between">
                  观看{movie.title}需要付费吗？
                  <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform"></i>
                </summary>
                <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                  不需要！丫丫影院所有影视资源完全免费，无需注册登录即可在线观看{movie.title}。
                </p>
              </details>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Movie',
            name: movie.title,
            description: movie.description,
            image: posterUrl,
            datePublished: movie.releaseDate,
            director: {
              '@type': 'Person',
              name: movie.director,
            },
            actor: movie.cast.map((a) => ({
              '@type': 'Person',
              name: a,
            })),
            genre: movie.tags,
            countryOfOrigin: {
              '@type': 'Country',
              name: movie.region,
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://yaya.tv/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: getCategoryLabel(movie.category),
                item: `https://yaya.tv/category/${movie.category}`,
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: movie.title,
              },
            ],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: `${movie.title}可以在线观看吗？`,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: `是的，${movie.title}可以在丫丫影院高清免费在线观看，播放流畅不缓冲。`,
                },
              },
              {
                '@type': 'Question',
                name: `${movie.title}的画质如何？`,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: `丫丫影院提供${movie.title}的高清画质在线观看，画面清晰细腻。`,
                },
              },
              {
                '@type': 'Question',
                name: `观看${movie.title}需要付费吗？`,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: `不需要！丫丫影院所有影视资源完全免费。`,
                },
              },
            ],
          }),
        }}
      />
    </>
  );
}